import React from 'react';
import clsx from 'clsx';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { toE164 } from '~/lib/phone';
import { DIRECTORY_PATH } from './voice-transfer-directory-changes';
import { VoiceAgentTransferDirectoryEntryType, VoiceAgentTransferCallType } from '~/api/graphql';
import type {
  VoiceAgentSettingsTransferDirectoryEntryInput,
  VoiceAgentTransferLineOfBusinessInput
} from './voice_agent_form_input';

export const INTERNAL_TRANSFER_CALL_TYPE_OPTIONS: [VoiceAgentTransferCallType, string][] = [
  [VoiceAgentTransferCallType.Claims, 'Claims'],
  [VoiceAgentTransferCallType.Billing, 'Billing'],
  [VoiceAgentTransferCallType.NewBusiness, 'New Business'],
  [VoiceAgentTransferCallType.Coi, 'COI, EOI, ID cards'],
  [VoiceAgentTransferCallType.Cancellation, 'Cancellation'],
  [VoiceAgentTransferCallType.OtherServicing, 'Other Servicing']
];

export const EXTERNAL_TRANSFER_CALL_TYPE_OPTIONS: [VoiceAgentTransferCallType, string][] = [
  [VoiceAgentTransferCallType.Claims, 'Claims'],
  [VoiceAgentTransferCallType.Billing, 'Billing'],
  [VoiceAgentTransferCallType.Coi, 'COI, EOI, ID cards'],
  [VoiceAgentTransferCallType.Cancellation, 'Cancellation'],
  [VoiceAgentTransferCallType.OtherServicing, 'Other Servicing']
];

type TransferDirectoryFieldAccess = {
  getFieldId: (path: (string | number)[]) => string;
  getValidationError: (path: string[]) => string | undefined;
};

function LineOfBusinessSelection({
  catalog,
  entry,
  disabled,
  onChange
}: {
  catalog: VoiceAgentTransferLineOfBusinessInput[];
  entry: VoiceAgentSettingsTransferDirectoryEntryInput;
  disabled: boolean;
  onChange: (lineOfBusinessIds: string[]) => void;
}) {
  return (
    <Field>
      <Label>Lines of Business (optional)</Label>
      <Description>
        Line of business is the outer routing segment; Call Type is nested within it. Leave all
        lines of business unchecked for this entry to handle every line of business.
      </Description>
      <CheckboxGroup className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
        {catalog.map((lineOfBusiness) => (
          <CheckboxField key={lineOfBusiness.id}>
            <Checkbox
              color="sky"
              disabled={disabled}
              checked={(entry.lineOfBusinessIds || []).includes(lineOfBusiness.id)}
              onChange={(checked) => {
                const current = entry.lineOfBusinessIds || [];
                onChange(
                  checked
                    ? [...current, lineOfBusiness.id]
                    : current.filter((id) => id !== lineOfBusiness.id)
                );
              }}
            />
            <Label>{lineOfBusiness.label}</Label>
          </CheckboxField>
        ))}
      </CheckboxGroup>
    </Field>
  );
}

function AliasList({
  entry,
  disabled,
  placeholder,
  description,
  onChange
}: {
  entry: VoiceAgentSettingsTransferDirectoryEntryInput;
  disabled: boolean;
  placeholder: string;
  description: React.ReactNode;
  onChange: (aliases: string[]) => void;
}) {
  const aliases = entry.aliases || [];

  return (
    <Field>
      <Label>Alternate names (optional)</Label>
      <Description>{description}</Description>
      <div className="mt-2 space-y-2">
        {aliases.map((alias, aliasIndex) => (
          <div key={aliasIndex} className="flex gap-2">
            <Input
              disabled={disabled}
              value={alias}
              onChange={(e) =>
                onChange(aliases.map((a, i) => (i === aliasIndex ? e.currentTarget.value : a)))
              }
              placeholder={placeholder}
            />
            <Button
              disabled={disabled}
              color="red"
              onClick={() => onChange(aliases.filter((_, i) => i !== aliasIndex))}
            >
              <XMarkIcon className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button disabled={disabled} color="sky" onClick={() => onChange([...aliases, ''])}>
          Add Alternate Name
        </Button>
      </div>
    </Field>
  );
}

export function TransferDirectoryEntryFields({
  index,
  entry,
  entryType,
  disabled,
  extensionEditable,
  lineOfBusinessCatalog,
  onChange,
  getFieldId,
  getValidationError
}: {
  index: number;
  entry: VoiceAgentSettingsTransferDirectoryEntryInput;
  entryType: VoiceAgentTransferDirectoryEntryType;
  disabled: boolean;
  extensionEditable: boolean;
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[];
  onChange: (patch: Partial<VoiceAgentSettingsTransferDirectoryEntryInput>) => void;
} & TransferDirectoryFieldAccess) {
  const path = (...rest: string[]) => [...DIRECTORY_PATH, String(index), ...rest];
  const errorFor = (field: string) => getValidationError(path(field));
  // Label is filled from Name, so a label error shows on Name.
  const nameError = errorFor('name') ?? errorFor('label');

  const phoneAndExtension = (
    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,18rem)_minmax(0,18rem)] md:gap-x-8 gap-y-4 md:items-start">
      <Field data-field-id={getFieldId(path('number'))}>
        <Label>Phone Number</Label>
        <Description>Include the country code, as in +1.</Description>
        <Input
          disabled={disabled}
          value={entry.number || ''}
          onChange={(e) => onChange({ number: e.currentTarget.value })}
          onBlur={() => {
            const formatted = toE164(entry.number || '');
            if (formatted && formatted !== entry.number) {
              onChange({ number: formatted });
            }
          }}
          placeholder="+1 (555) 123-4567"
          invalid={!!errorFor('number')}
        />
        {errorFor('number') && <ErrorMessage>{errorFor('number')}</ErrorMessage>}
      </Field>

      <Field className={clsx(!extensionEditable && 'opacity-50 pointer-events-none select-none')}>
        <Label>Extension (optional)</Label>
        <Description>Dialed after the call connects.</Description>
        <Input
          disabled={disabled || !extensionEditable}
          value={entry.extension || ''}
          onChange={(e) => onChange({ extension: e.currentTarget.value || undefined })}
          placeholder="101"
        />
        {/* Below the input so the two columns' inputs line up. */}
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          Used for warm and agentic transfers, and for cold transfers when SIP mode is INVITE.
        </p>
      </Field>
    </div>
  );

  const lineOfBusiness = (
    <LineOfBusinessSelection
      catalog={lineOfBusinessCatalog}
      entry={entry}
      disabled={disabled}
      onChange={(lineOfBusinessIds) => onChange({ lineOfBusinessIds })}
    />
  );

  const callTypeCheckboxes = (options: [VoiceAgentTransferCallType, string][]) => (
    <CheckboxGroup className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
      {options.map(([value, label]) => (
        <CheckboxField key={value}>
          <Checkbox
            color="sky"
            disabled={disabled}
            checked={(entry.callTypes || []).includes(value)}
            onChange={(checked) => {
              const current = entry.callTypes || [];
              onChange({
                callTypes: checked ? [...current, value] : current.filter((ct) => ct !== value)
              });
            }}
          />
          <Label>{label}</Label>
        </CheckboxField>
      ))}
    </CheckboxGroup>
  );

  if (entryType === VoiceAgentTransferDirectoryEntryType.External) {
    return (
      <div className="space-y-3">
        <Field data-field-id={getFieldId(path('name'))}>
          <Label>Name</Label>
          <Description>
            Configure the name Cara uses to match this party. At call start, carrier names from your
            AMS are loaded automatically via lookup_customer (primary match signal). Use the name
            your AMS shows, such as &quot;State Farm&quot;, so Cara can match confidently when
            transferring. Callers can also name a carrier verbally as a secondary signal.
          </Description>
          <Input
            disabled={disabled}
            value={entry.name || ''}
            onChange={(e) => onChange({ name: e.currentTarget.value || undefined })}
            placeholder="e.g., State Farm"
            invalid={!!nameError}
          />
          {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
        </Field>

        {phoneAndExtension}
        {lineOfBusiness}

        <Field data-field-id={getFieldId(path('callTypes'))}>
          <Label>Call Types (required)</Label>
          <Description>
            Select which call types may route to this external number. Required to enable external
            transfers for those types.
          </Description>
          {callTypeCheckboxes(EXTERNAL_TRANSFER_CALL_TYPE_OPTIONS)}
          {errorFor('callTypes') && <ErrorMessage>{errorFor('callTypes')}</ErrorMessage>}
        </Field>

        <AliasList
          entry={entry}
          disabled={disabled}
          placeholder="e.g., State Farm Mutual"
          description="Add other names Cara might hear for this party, such as a writing company name, a carrier parent name, or another label from your AMS. Use a separate field for each alias."
          onChange={(aliases) => onChange({ aliases })}
        />
        {errorFor('aliases') && <ErrorMessage>{errorFor('aliases')}</ErrorMessage>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Field data-field-id={getFieldId(path('name'))}>
        <Label>Name</Label>
        <Description>The person&apos;s name (e.g. &quot;Joe Becker&quot;). Required.</Description>
        <Input
          disabled={disabled}
          value={entry.name || ''}
          onChange={(e) => onChange({ name: e.currentTarget.value || undefined })}
          placeholder="e.g., Joe Becker"
          invalid={!!nameError}
        />
        {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
      </Field>

      {phoneAndExtension}

      <Field>
        <Label>Servicing code (optional)</Label>
        <Description>
          Person code for this agent in your AMS (e.g. CSR in AMS360). When provided, callers
          matched by servicing code are routed here first.
        </Description>
        <Input
          disabled={disabled}
          value={entry.servicingCode || ''}
          onChange={(e) => onChange({ servicingCode: e.currentTarget.value || undefined })}
          placeholder="e.g., ABC123"
        />
      </Field>

      {lineOfBusiness}

      <Field>
        <Label>Call Types (optional)</Label>
        <Description>
          Limit this entry to specific call types. Leave empty to handle all call types.
        </Description>
        {callTypeCheckboxes(INTERNAL_TRANSFER_CALL_TYPE_OPTIONS)}
      </Field>

      <AliasList
        entry={entry}
        disabled={disabled}
        placeholder="e.g., Ben"
        description='Add how callers might ask for this person or team when it does not match the name above: for example, "Ben" when they are listed as Benjamin, or the name clients use if they only know a short form. Several names in one field, separated by commas, are still stored as a single alias, so use Add Alternate Name for each nickname.'
        onChange={(aliases) => onChange({ aliases })}
      />
      {errorFor('aliases') && <ErrorMessage>{errorFor('aliases')}</ErrorMessage>}
    </div>
  );
}
